import React from 'react';
import { Sparkles } from 'lucide-react';
import { useLocaleContext } from '../context/LocaleContext';
import { UserAvatarStack } from './UserAvatarStack';
import type { Locale } from '../types/locale';

interface SocialProofProps {
  className?: string;
}

/**
 * Reusable social proof component showing:
 * - User avatar stack with trust message
 * - Professional AI text cleaning badge
 * - Country badge removed to improve performance
 */
export const SocialProof: React.FC<SocialProofProps> = ({ className = '' }) => {
  const { locale } = useLocaleContext();

  // Locale-optimized translations for SEO
  const professionalBadgeText: Record<Locale, string> = {
    en: 'Professional AI Text Cleaning',
    de: 'Professionelle KI-Textreinigung',
    'pt-BR': 'Limpeza Profissional de Texto IA',
    'ms-MY': 'Pembersihan Teks AI Profesional',
    es: 'Limpieza Profesional de Textos con IA',
    fr: 'Nettoyage Professionnel de Texte par IA',
    ja: 'プロフェッショナルAIテキストクリーニング',
    id: 'Pembersihan Teks AI Profesional',
  };

  return (
    // CLS FIX: min-h ensures consistent height across all render states
    <div className={`flex flex-col gap-4 min-h-[100px] ${className}`}>
      {/* User Avatar Stack */}
      <div className="flex justify-center lg:justify-start min-h-[44px]">
        <UserAvatarStack maxAvatars={5} />
      </div>

      {/* Chips */}
      <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 min-h-[36px]">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 dark:bg-accent-900/30 rounded-full text-accent-700 dark:text-accent-300 text-sm font-medium border border-accent-200 dark:border-accent-800">
          <Sparkles size={16} />
          <span>{professionalBadgeText[locale]}</span>
        </div>
      </div>
    </div>
  );
};
